import { Button, Card, Carousel, Collapse, Image, Row } from "antd";
import { CarouselRef } from "antd/es/carousel";
import { useRef } from "react";
import Icon from "../../../assets/icons/icon";
import CustomCollapse from "../../../components/customCollapse";
import InputVin from "../../../components/form/inputVin";
import LinkBox from "../../../components/form/linkBox";
import _t from "../../../lang/translate";
import { SourceVehiclePreview } from "../../../types/motorAtlasCatalogTypes";
import format from "../../../utilities/formatNumbers";
import { TinySpace, Value, VehicleSpecCol } from "./components";
import { ModelMatcherInputVehicle } from "./util";


const { Panel } = Collapse

type Props = {
  sourceVehicle: SourceVehiclePreview | null
  inputVehicle: ModelMatcherInputVehicle,
}
export default function VehicleDetails({ sourceVehicle: vehicle, inputVehicle }: Props) {
  const carouselRef = useRef<CarouselRef>(null);
  function handleNext() {
    carouselRef.current?.next()
  }
  function handlePrev() {
    carouselRef.current?.prev()
  }

  return (
    <Card style={{ position: "sticky", top: 16 }} bodyStyle={{ padding: "12px 16px" }}>
      <h3 className="mb-1">Vurderingskøretøjet</h3>
      <div className="mb-05">
        {inputVehicle?.link && (
          <LinkBox link={inputVehicle?.link} />
        )}
        {inputVehicle?.vin && (
          <InputVin disabled value={inputVehicle?.vin} />
        )}
      </div>
      <CustomCollapse defaultActiveKey={"vehicleData"} className="" style={{ marginLeft: 0, marginRight: 0 }}>
        <Panel key="vehicleData" header={
          <div>
            <span style={{
              marginRight: 8,
              display: "inline-block",
              height: 6,
              width: 6,
              borderRadius: 99,
              backgroundColor: (!!vehicle?.core || !!vehicle?.technical) ? "#52C41A" : "#FF2424"
            }}></span>
            <span className="text-small" style={{ lineHeight: "24px" }}>Data</span>
          </div>
        }>
          <VehicleData
            core={vehicle?.core}
            technical={vehicle?.technical}
            recommendedFilters={vehicle?.recommendedFilters} />
        </Panel>
        <Panel key="dmrVehicleData" header={
          <div>
            <span style={{
              marginRight: 8,
              display: "inline-block",
              height: 6,
              width: 6,
              borderRadius: 99,
              backgroundColor: (!!vehicle?.supplementary.dmr?.core || !!vehicle?.supplementary.dmr?.technical) ? "#52C41A" : "#FF2424"
            }}></span>
            <span className="text-small" style={{ lineHeight: "24px" }}>DMR</span>
          </div>
        }>
          <VehicleData core={vehicle?.supplementary.dmr?.core} technical={vehicle?.supplementary.dmr?.technical} recommendedFilters={vehicle?.recommendedFilters} />
        </Panel>
        <Panel key="images" header={
          <div>
            <span style={{
              marginRight: 8,
              display: "inline-block",
              height: 6,
              width: 6,
              borderRadius: 99,
              backgroundColor: !!vehicle?.supplementary?.images?.length ? "#52C41A" : "#FF2424"
            }}></span>
            <span className="text-small" style={{ lineHeight: "24px" }}>Billeder</span>
          </div>
        }>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Button shape="circle" size="small" onClick={handlePrev}><Icon name="arrow-back-outline" /></Button>
            <div style={{ flex: "1 1 0", minWidth: 0, overflow: "hidden" }}>
              <Image.PreviewGroup>
                <Carousel ref={carouselRef}
                  infinite
                  centerMode
                  slidesToScroll={1}
                  slidesToShow={2}
                  variableWidth={false}
                  waitForAnimate={false}
                  className="model-catalog-image-carousel"
                >
                  {vehicle?.supplementary?.images?.map((image, index) => (
                    <Image key={index} src={image} />
                  ))}
                </Carousel>
              </Image.PreviewGroup>
            </div>
            <Button shape="circle" size="small" onClick={handleNext}><Icon name="arrow-forward-outline" /></Button>
          </div>
        </Panel>
        <Panel key="equipment" header={
          <div>
            <span style={{
              marginRight: 8,
              display: "inline-block",
              height: 6,
              width: 6,
              borderRadius: 99,
              backgroundColor: !!vehicle?.supplementary.features ? "#52C41A" : "#FF2424"
            }}></span>
            <span className="text-small" style={{ lineHeight: "24px" }}>Udstyr</span>
          </div>
        }>
          <div className="">
            {vehicle?.supplementary?.features?.map((item, index) => (
              <p key={item + index} className="mb-0 ">{item}</p>
            ))}
          </div>
        </Panel>
        <Panel key="description" header={
          <div>
            <span style={{
              marginRight: 8,
              display: "inline-block",
              height: 6,
              width: 6,
              borderRadius: 99,
              backgroundColor: !!vehicle?.supplementary?.description ? "#52C41A" : "#FF2424"
            }}></span>
            <span className="text-small" style={{ lineHeight: "24px" }}>Beskrivelse</span>
          </div>
        }>
          <p className="">
            {vehicle?.supplementary?.description}
          </p>
        </Panel>
      </CustomCollapse>

    </Card>
  )
}


function VehicleData(vehicle: Partial<Pick<SourceVehiclePreview, "core" | "technical" | "recommendedFilters">>) {
  return (
    <>
      <h4>Bildata</h4>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2 }}>
        <TinySpace tooltip={_t('brand')}>
          <Icon name="buildings" size="large" color="#0299FB" />
          <Value value={vehicle.core?.make} />
        </TinySpace>
        <TinySpace tooltip={_t('model')}>
          <Icon name="car" size="large" color="#0299FB" />
          <Value value={vehicle.core?.model} />
        </TinySpace>
        <TinySpace tooltip={_t('subModel')}>
          <Icon name="package" size="large" color="#0299FB" />
          <Value value={vehicle.core?.subModel} />
        </TinySpace>
        <TinySpace tooltip={_t('variant')}>
          <Icon name="tag" size="large" color="#0299FB" />
          <Value value={vehicle.core?.variant} />
        </TinySpace>
        <TinySpace tooltip={_t('description')}>
          <Icon name="file-text" size="large" color="#0299FB" />
          <Value value={vehicle.core?.marketDescription} />
        </TinySpace>
      </div>
      <h4 className="mt-1">{_t("technical_data")}</h4>
      <Row wrap justify="space-between" gutter={[24, 16]}>
        <VehicleSpecCol>
          <TinySpace tooltip={_t('firstRegDate')}>
            <Icon name="calendar-blank" size="large" color="#B37FEB" />
            <Value value={format.date(vehicle.core?.firstRegDate)} />
          </TinySpace>
          <TinySpace tooltip={_t('drive_wheel')}>
            <Icon name="git-commit" size="large" color="#B37FEB" />
            <Value value={vehicle.technical?.driveType} />
          </TinySpace>
        </VehicleSpecCol>
        <VehicleSpecCol>
          <TinySpace tooltip={_t('fuel')}>
            <Icon name="gas-pump" size="large" color="#B37FEB" />
            <Value value={vehicle.technical?.fuelType} />
          </TinySpace>
          <TinySpace tooltip={_t('gearbox')}>
            <Icon name="gear" size="large" color="#B37FEB" />
            <Value value={vehicle.technical?.gearbox} />
          </TinySpace>
        </VehicleSpecCol>
        <VehicleSpecCol>
          <TinySpace tooltip={_t('engine_size')}>
            <Icon name="wrench" size="large" color="#B37FEB" />
            <Value value={vehicle.technical?.engineSize} />
          </TinySpace>
          <TinySpace tooltip={_t('horse_power')}>
            <Icon name="gauge" size="large" color="#B37FEB" />
            <Value value={vehicle.technical?.horsePower} suffix={"hk"} />
          </TinySpace>
        </VehicleSpecCol>
      </Row>
      <h4 className="mt-1">{_t("environment", "physical", "/")}</h4>
      <Row wrap justify="space-between" gutter={[24, 16]}>
        <VehicleSpecCol>
          <TinySpace tooltip={_t('trim')}>
            <Icon name="car-simple" size="large" color="#389E0F" />
            <Value value={vehicle.technical?.bodyType} />
          </TinySpace>
          <TinySpace tooltip={_t("battery_capacity")}>
            <Icon name="battery-full" size="large" color="#389E0F" />
            <Value value={vehicle.technical?.batteryCapacity} suffix={"kWh"} />
          </TinySpace>

        </VehicleSpecCol>
        <VehicleSpecCol>
          <TinySpace tooltip={_t('doors')}>
            <Icon name="door" size="large" color="#389E0F" />
            <Value value={vehicle.technical?.doors} />
          </TinySpace>
          <TinySpace tooltip="Rækkevidde">
            <Icon name="crosshair-simple" size="large" color="#389E0F" />
            <Value value={vehicle.technical?.electricRange} suffix={"km"} />
          </TinySpace>
        </VehicleSpecCol>
        <VehicleSpecCol>
          <TinySpace tooltip="CO2">
            <Icon name="leaf" size="large" color="#389E0F" />
            <Value value={vehicle.technical?.co2Emission} suffix={"g/km"} />
          </TinySpace>
        </VehicleSpecCol>
      </Row>
    </>

  )
}

